<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { resolvePriceCurrency } from 'dashboard/helper/currency';
import { resolveEventListPrice } from 'dashboard/routes/dashboard/planning/events/eventListPrice';
import Icon from 'next/icon/Icon.vue';
import { formatTime } from 'shared/helpers/formatTime';
import { resolveEventAccentColor } from 'dashboard/routes/dashboard/planning/events/useEventStageColors';
import { getEventDateRange } from 'dashboard/routes/dashboard/planning/events/getEventDateRange';
import EventsAPI from 'dashboard/api/planning/events';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';
import EventLastChangeLine from 'dashboard/routes/dashboard/planning/events/components/EventLastChangeLine.vue';

const props = defineProps({
  event: {
    type: Object,
    required: true,
  },
  active: {
    type: Boolean,
    default: false,
  },
  showLastChange: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const router = useRouter();
const { accountId, currentAccount } = useAccount();

const resolvedPrice = ref(null);
const isLoadingPrice = ref(false);

const isUnread = computed(() => Boolean(props.event.unread));
const accentColor = computed(() => resolveEventAccentColor(props.event));
const allocationDateRange = computed(() => getEventDateRange(props.event));

const savedPrice = computed(() => {
  const fromColumn = props.event.current_total_price;
  if (fromColumn !== null && fromColumn !== undefined && fromColumn !== '') {
    return Number(fromColumn);
  }
  const fromBreakdown =
    props.event.metadata?.price_breakdown?.total_excl_tax ??
    props.event.metadata?.price_breakdown?.totalExclTax;
  if (
    fromBreakdown !== null &&
    fromBreakdown !== undefined &&
    fromBreakdown !== ''
  ) {
    return Number(fromBreakdown);
  }
  return null;
});

const displayPrice = computed(() => {
  const value = savedPrice.value ?? resolvedPrice.value;
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  if (Number(value) === 0) return null;
  return value;
});

const formattedPrice = computed(() => {
  const currency = resolvePriceCurrency({
    breakdownCurrency:
      props.event.metadata?.price_breakdown?.currency || props.event.currency,
    account: currentAccount.value,
  });
  return resolveEventListPrice({
    acceptedTotal: props.event.accepted_quote_total,
    quotedMin: props.event.quoted_price_min,
    quotedMax: props.event.quoted_price_max,
    fallbackTotal: displayPrice.value,
    currency,
  });
});

const location = computed(() => {
  if (
    props.event.allocated_resources &&
    props.event.allocated_resources.length > 0
  ) {
    return props.event.allocated_resources[0].name;
  }
  return props.event.custom_attributes?.location || 'TBA';
});

const date = computed(() => {
  const startDate = allocationDateRange.value?.start || null;
  const endDate = allocationDateRange.value?.end || null;

  if (!startDate || Number.isNaN(startDate.getTime())) return 'TBD';

  const startFormatted = startDate.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
  });

  if (!endDate || startDate.toDateString() === endDate.toDateString()) {
    return startFormatted;
  }

  const endDay = endDate.getDate();
  const endMonth = endDate.toLocaleDateString('en-US', { month: 'short' });
  const startMonth = startDate.toLocaleDateString('en-US', { month: 'short' });

  if (startMonth === endMonth) {
    return `${startDate.getDate()}-${endDay} ${endMonth}`;
  }
  return `${startMonth} ${startDate.getDate()} - ${endMonth} ${endDay}`;
});

const time = computed(() => {
  const range = allocationDateRange.value;
  if (!range?.start || !range?.end) return 'TBA';
  if (
    Number.isNaN(range.start.getTime()) ||
    Number.isNaN(range.end.getTime())
  ) {
    return 'TBA';
  }

  const s = range.start;
  const e = range.end;
  if (
    s.getHours() === 0 &&
    s.getMinutes() === 0 &&
    s.getSeconds() === 0 &&
    e.getHours() === 23 &&
    e.getMinutes() === 59 &&
    e.getSeconds() >= 59 &&
    s.toDateString() === e.toDateString()
  ) {
    return t('PLANNING.EVENTS_CARD.ALL_DAY');
  }

  return `${formatTime(range.start)} - ${formatTime(range.end)}`;
});

const toUnixSeconds = value => {
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? 0 : Math.round(ms / 1000);
};

const updatedAtTimestamp = computed(() =>
  toUnixSeconds(props.event.updated_at || props.event.created_at)
);

const createdAtTimestamp = computed(() =>
  toUnixSeconds(props.event.created_at)
);

const pax = computed(() => {
  const value =
    props.event.pax ??
    props.event.custom_attributes?.pax ??
    props.event.metadata?.pax;
  if (value === null || value === undefined || value === '') return null;
  return value;
});

const organization = computed(
  () =>
    props.event.customer?.name ||
    props.event.custom_attributes?.organization ||
    null
);

const loadPriceIfNeeded = async () => {
  if (
    props.event.accepted_quote_total != null ||
    (props.event.quoted_price_min != null &&
      props.event.quoted_price_max != null)
  ) {
    resolvedPrice.value = null;
    return;
  }
  if (savedPrice.value !== null) {
    resolvedPrice.value = null;
    return;
  }
  if (!props.event?.id || isLoadingPrice.value) return;

  isLoadingPrice.value = true;
  try {
    const response = await EventsAPI.calculatePrice(props.event.id);
    const total =
      response.data?.total_excl_tax ??
      response.data?.totalExclTax ??
      response.data?.current_total_price;
    resolvedPrice.value =
      total === null || total === undefined ? null : Number(total);
  } catch {
    resolvedPrice.value = null;
  } finally {
    isLoadingPrice.value = false;
  }
};

const onSelect = () => {
  router.push({
    name: 'event_inbox_event',
    params: { accountId: accountId.value, eventId: props.event.id },
  });
};

watch(
  () => props.event.id,
  () => {
    resolvedPrice.value = null;
    loadPriceIfNeeded();
  }
);

onMounted(loadPriceIfNeeded);
</script>

<template>
  <button
    type="button"
    class="rounded-none relative flex items-start flex-grow-0 flex-shrink-0 w-auto max-w-full py-0 border-t-0 border-b-0 border-l-0 border-r-0 border-transparent border-solid cursor-pointer conversation hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3 group px-3"
    :class="{
      '!bg-n-background': active,
    }"
    @click="onSelect"
  >
    <span
      class="pointer-events-none absolute left-2 top-2.5 bottom-2.5 w-1.5 rounded-full"
      :style="{ backgroundColor: accentColor }"
    />
    <div class="pl-6 pr-3 py-3 flex flex-col gap-1.5 min-w-0 flex-1">
      <div class="flex items-start justify-between gap-2 min-w-0">
        <h3
          class="text-sm text-slate-900 dark:text-white truncate mb-0 min-w-0"
          :class="isUnread ? 'font-bold' : 'font-semibold'"
        >
          {{ event.name }}
        </h3>
        <div class="flex items-center gap-1.5 shrink-0">
          <TimeAgo
            :last-activity-timestamp="updatedAtTimestamp"
            :created-at-timestamp="createdAtTimestamp"
          />
          <span
            v-if="isUnread"
            class="size-2 rounded-full bg-n-brand flex-shrink-0"
            :aria-label="t('PLANNING.EVENTS_CARD.UNREAD')"
          />
        </div>
      </div>

      <div
        class="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs text-slate-500 dark:text-slate-400"
      >
        <span class="inline-flex items-center gap-1 min-w-0">
          <Icon icon="i-lucide-map-pin" class="size-3 shrink-0" />
          <span class="truncate">{{ location }}</span>
        </span>
        <span class="inline-flex items-center gap-1">
          <Icon icon="i-lucide-calendar" class="size-3 shrink-0" />
          <span>{{ date }}</span>
        </span>
        <span class="inline-flex items-center gap-1">
          <Icon icon="i-lucide-clock" class="size-3 shrink-0" />
          <span>{{ time }}</span>
        </span>
        <span v-if="pax" class="inline-flex items-center gap-1">
          <Icon icon="i-lucide-users" class="size-3 shrink-0" />
          <span>{{ pax }}</span>
        </span>
        <span
          v-if="organization"
          class="inline-flex items-center gap-1 min-w-0"
        >
          <Icon icon="i-lucide-briefcase" class="size-3 shrink-0" />
          <span class="truncate">{{ organization }}</span>
        </span>
      </div>
      <div class="flex items-center gap-1">
        <span
          v-if="formattedPrice"
          class="inline-flex items-center gap-1 font-bold text-slate-900 dark:text-white"
        >
          <span>{{ formattedPrice }}</span>
        </span>
      </div>
      <EventLastChangeLine
        v-if="showLastChange"
        :last-change="event.last_change"
        compact
      />
    </div>
  </button>
</template>
